import { formatDistanceToNow } from 'date-fns';
import type { Strategy } from '@/features/Strategies/types';
import type { User } from '../types';

export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const getFullName = (user: User) => `${user.firstName} ${user.lastName}`;

// Translucent status ramp ported 1:1 from the Figma export
// (StrategyHub.tsx getBadgeStyles). Pulse lives on the DOT, not the badge.
//
// THE `dark:` HALVES ARE LOAD-BEARING, and this file is the reason a component
// can pass the theme-token guard and still be unreadable: `StatusBadge` lives
// in `features/Strategies`, which is guarded, but its classes come from HERE.
// The guard scans converted directories, so a literal imported from an
// unconverted helper walks straight through it. `shared` is on that list now.
//
// The `-500/10` fills and `-500/20` borders are fine on either ground: a
// saturated hue at 10% is a tint of whatever is behind it. The TEXT was not —
// `text-green-700` and friends are dark inks, so a badge on a dark page was
// dark-on-dark. Lightening the ink in dark mode rather than swapping the hue is
// the convention already used by `features/Clients/clientStatus.tsx` and
// `ClassificationTypeBadge`, and keeping the three in step matters more than
// any of them being individually clever — they are the same status vocabulary.
export const getStatusColor = (status: Strategy['status']) => {
  switch (status) {
    case 'live':
      return 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20';
    // Neutral rather than a hue, so it takes the token outright — the same
    // treatment clientStatus gives its own slate badge.
    case 'backtest':
      return 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20';
    case 'archived':
      return 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20';
    case 'configuration':
      return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20';
    case 'running':
      return 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20';
    // Deliberately heavier than `archived`: both are red, and a failed run
    // needs to outweigh one somebody chose to shelve. It was `bg-red-100`, an
    // opaque light fill that stayed light on a dark page; the same weight
    // expressed as an alpha follows the surface instead.
    case 'failed':
      return 'bg-red-500/20 text-red-700 dark:text-red-200 border-red-500/30';
    default:
      return 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20';
  }
};

// Dot colour + pulse for the status pill. Kept in lockstep with
// getStatusColor above so the dot always matches its badge tint.
export const getStatusDotColor = (status: Strategy['status']) => {
  switch (status) {
    case 'live':
      return 'bg-green-500 animate-pulse';
    case 'running':
      return 'bg-orange-500 animate-pulse';
    case 'configuration':
      return 'bg-blue-500';
    case 'archived':
    case 'failed':
      return 'bg-red-500';
    case 'backtest':
    default:
      return 'bg-muted-foreground';
  }
};

export const getStatusName = (status: Strategy['status']) => {
  switch (status) {
    case 'live':
      return 'Live Model';
    case 'backtest':
      return 'Backtest';
    case 'archived':
      return 'Archived';
    case 'configuration':
      return 'Draft Model';
    case 'running':
      return 'In Progress';
    case 'failed':
      return 'Failed Run';
    default:
      return 'Live Model';
  }
};

export const formattedDate = (lastUpdated: string) => {
  try {
    return formatDistanceToNow(new Date(lastUpdated), { addSuffix: true });
  } catch (e) {
    return lastUpdated;
  }
};

export const generateDuplicateName = (
  originalName: string,
  existingNames: Array<string>,
) => {
  const baseNameRegex = / \(Copy(\s\d+)?\)$/;
  const baseName = originalName.replace(baseNameRegex, '');
  const namesSet = new Set(existingNames);

  let candidate = `${baseName} (Copy)`;
  if (!namesSet.has(candidate)) return candidate;

  let counter = 1;
  for (;;) {
    candidate = `${baseName} (Copy ${counter})`;
    if (!namesSet.has(candidate)) return candidate;
    counter++;
  }
};

// Prevents timezone shifting issues by parsing YYYY-MM-DD as local midnight
export const parseLocalDate = (
  dateStr: string | undefined | null,
): Date | undefined => {
  if (!dateStr) return undefined;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const getAppRouteString = (extension: string) => {
  return `/${extension}`;
};

/** Derive a short ticker-like code from a name (e.g. "S&P 500" → "SP500"). */
export const toCode = (name: string): string => {
  const cleaned = name.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  const words = cleaned.split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 5).toUpperCase();
  return words
    .map((w) => (/^\d+$/.test(w) ? w : w[0]))
    .join('')
    .toUpperCase();
};
